import React from "react";
import { Tooltip } from "antd";
import { getBaseColumns, getPacsSpecificColumns, renderActionColumn, calculateActionsColumnWidth } from "../../utils/sharedTableColumns";
import { getUserDetails } from "../../utils/helper";
import { FieldLabels } from "../../constants/fieldLabels";

export const orderColumns = ({
  openViewer,
  showOrdDetails,
  openRadDesk,
  openReportEditor,
  role,
  addFile,
  viewNotes,
  printReport,
  downloadDocxReport,
  viewReport,
  editSignedOffReport,
  assignToSelfTechnician,
  toggleReporting,
  toggleConfirmation,
  toggleFeatures,
  markMlc,
  handleShareStudy,
  handleDownloadStudy,
  handleExportStudy,
  editStudyDetails,
  showStudyHistory,
  handleSelectSeries,
  splitStudy,
  handleRadiologistAssignment,
  userList,
  openRadiantViewer,
  enableRadiantViewer,
  deleteOrder,
  canDeleteOrder
}) => {
  const userDetails = getUserDetails();
  const baseColumns = getBaseColumns();
  const pacsSpecificColumns = getPacsSpecificColumns();

  // Customize patient name column to include openReportEditor and Radiant Viewer
  const patientNameColumn = baseColumns.find(col => col.key === 'pat_name');
  if (patientNameColumn) {
    const originalRender = patientNameColumn.render;
    patientNameColumn.render = (text, record) => originalRender(text, record, openReportEditor, openViewer, openRadiantViewer, enableRadiantViewer);
  }

  // Customize technician assignment column
  const techAssignColumn = pacsSpecificColumns.find(col => col.key === 'assigned_to_tech');
  if (techAssignColumn) {
    const originalRender = techAssignColumn.render;
    techAssignColumn.render = (text, record, ind) => originalRender(text, record, ind, assignToSelfTechnician);
  }

  // Customize radiologist assignment column
  const radAssignColumn = pacsSpecificColumns.find(col => col.key === 'assigned_radiologist');
  if (radAssignColumn) {
    const originalRender = radAssignColumn.render;
    radAssignColumn.render = (text, record, ind) => originalRender(text, record, ind, handleRadiologistAssignment, userList);
  }

  // Action column configuration
  const actionConfig = {
    openViewer,
    showOrdDetails,
    openRadDesk,
    openReportEditor,
    addFile,
    viewNotes,
    printReport,
    downloadDocxReport,
    viewReport,
    editSignedOffReport,
    handleShareStudy,
    handleDownloadStudy,
    handleExportStudy,
    editStudyDetails,
    showStudyHistory,
    handleSelectSeries,
    splitStudy,
    assignToSelfTechnician,
    toggleReporting,
    toggleConfirmation,
    toggleFeatures,
    markMlc,
    deleteOrder,
    canDeleteOrder,
    role,
    pageType: 'pacs'
  };

  const actionsColumn = {
    title: FieldLabels.ACTIONS,
    key: "actions_column",
    render: renderActionColumn(actionConfig),
    width: calculateActionsColumnWidth(actionConfig),
  };

  const isTechnician = userDetails?.user_type === 'technician';

  if (isTechnician) {
    // ---- Technician PACS list: column order + compact sizing mirroring the
    // reference RIS study list. Scoped here so the shared base columns (reused by
    // the Worklist and the other PACS roles) stay untouched. The objects in
    // baseColumns / pacsSpecificColumns are created fresh on every call, so
    // mutating width / fixed / hidden below is safe.
    const byKey: Record<string, any> = {};
    [...baseColumns, ...pacsSpecificColumns].forEach(c => {
      byKey[c.key || c.dataIndex] = c;
    });

    // Reference shows AE-Title for technicians (it's hidden for them by default).
    if (byKey['sending_aet']) byKey['sending_aet'].hidden = false;

    // Un-pin the Status badge so it sits inline in the reference position
    // (reference puts status mid-table with Assigned as the last column).
    if (byKey['status']) {
      byKey['status'].fixed = undefined;
      byKey['status'].className = undefined;
    }

    const ellipsis: React.CSSProperties = {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      width: '100%',
    };

    // New columns sourced from data the PACS API already returns.
    byKey['po_site'] = {
      dataIndex: 'po_site',
      key: 'po_site',
      title: FieldLabels.SITE,
      width: 110,
      render: (_t, rec) => {
        const site = rec?.pacs_order?.po_site || 'SOMAJIGUDA';
        return <Tooltip title={site}><div style={ellipsis}>{site}</div></Tooltip>;
      },
    };
    byKey['po_visit_number'] = {
      dataIndex: 'po_visit_number',
      key: 'po_visit_number',
      title: FieldLabels.VISIT_ID,
      width: 100,
      render: (_t, rec) => {
        const visit = rec?.pacs_order?.po_visit_number || '';
        return <Tooltip title={visit}><div style={ellipsis}>{visit}</div></Tooltip>;
      },
    };

    // Compact widths, mirroring the Orders page density.
    const widths: Record<string, number> = {
      pat_name: 180, po_pin: 90, po_his_status: 120, pat_age: 56, pat_sex: 48,
      body_part: 110, diag_desc: 180, po_site: 110, sending_aet: 110, modality: 74,
      po_ord_no: 94, po_visit_number: 100, acc_no: 102, ref_doc: 138,
      study_dt_tm: 150, scan_received_at: 150, status: 110, assigned_to_tech: 140,
      ps_series_count: 90, live_report_status: 30, assigned_radiologist: 150,
    };

    // Reference column order, followed by the extras we keep that the reference
    // doesn't have (series count, the reporting-status dot, assigned-radiologist).
    const referenceOrder = [
      'pat_name', 'po_pin', 'po_his_status', 'pat_age', 'pat_sex',
      'body_part', 'diag_desc', 'po_site', 'sending_aet', 'modality',
      'po_ord_no', 'po_visit_number', 'acc_no', 'ref_doc',
      'study_dt_tm', 'scan_received_at', 'status', 'assigned_to_tech',
    ];
    const extras = ['ps_series_count', 'live_report_status', 'assigned_radiologist'];

    return [...referenceOrder, ...extras]
      .map(k => byKey[k])
      .filter(col => col && !col.hidden)
      .map(col => {
        const w = widths[col.key || col.dataIndex];
        if (w != null) col.width = w;
        return col;
      });
  }

  return [
    ...baseColumns,
    ...pacsSpecificColumns,
    ...(isTechnician ? [] : [actionsColumn]),
  ].filter(itm => !itm.hidden);
};
